import React from 'react';

import { Alert, Button, Center, Group, Skeleton, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import useAthlete from 'hooks/athlete/useAthlete';
import useDeleteAthlete from 'hooks/athlete/useDeleteAthlete';
import { useNavigate } from 'react-router-dom';
import notificationHandler from 'utils/notifications';

interface IDeleteAthleteModalProps {
  athleteId?: string;
  handleClose: () => void;
  redirect?: boolean;
}

const DeleteAthleteModal = ({ athleteId, handleClose, redirect }: IDeleteAthleteModalProps): JSX.Element => {
  const deleteAthleteMutation = useDeleteAthlete();
  const navigate = useNavigate();
  const {
    data: userData,
    isSuccess: userIsSuccess,
    isError: userIsError,
    isLoading: userIsLoading,
  } = useAthlete(athleteId);
  const theme = useMantineTheme();

  const handleDelete = (): void => {
    const { successNotification, errorNotification } = notificationHandler();
    if (userIsSuccess && userData) {
      deleteAthleteMutation.mutate(userData.data.athleteId, {
        onSuccess: () => {
          successNotification(
            'Athlete delete success',
            `${userData.data.firstName} ${userData.data.lastName} successfully deleted.`,
          );
          handleClose();
          if (redirect == true) {
            navigate('/users/athletes');
          }
        },
        onError: error => {
          errorNotification('Athlete delete failed', error.response?.data.message);
        },
      });
    }
  };

  if (userIsError) {
    return (
      <Center sx={{ flexGrow: 1 }} p='lg'>
        <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
          Something went wrong while fetching data!
        </Alert>
      </Center>
    );
  }
  return (
    <Stack>
      {userIsSuccess && userData && (
        <>
          <Text>{`Are you sure you want to delete ${userData?.data.firstName} ${userData?.data.lastName}?`}</Text>
          <Text color='dimmed'>This action cannot be undone.</Text>
          <Group position='right'>
            <Button
              onClick={handleDelete}
              color='red'
              disabled={userIsLoading}
              loading={deleteAthleteMutation.isLoading}
              data-cy='deleteAthleteButton'
            >
              Delete
            </Button>
          </Group>
        </>
      )}
      {userIsLoading && (
        <>
          <Skeleton w='100%' h='md' mb='xs' />
          <Skeleton w='100%' h='md' />
        </>
      )}
    </Stack>
  );
};

export default DeleteAthleteModal;
